import React, { FormEvent, useEffect, useState } from 'react';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RotateCcw, Save } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import type { UserDisplaySettings } from '@/models/user';

type ActiveWorkspace = UserDisplaySettings['activeWorkspace'];

interface TipPolicyCardProps {
  workspaceId: string;
  activeWorkspace: ActiveWorkspace;
  onUpdate: () => Promise<void>;
}

const VERTICAL_ROWS = [
  { key: 'tattoo', label: 'Tattoo' },
  { key: 'beauty', label: 'Beauty' },
  { key: 'aesthetics', label: 'Aesthetics' },
  { key: 'salon', label: 'Salon' },
  { key: 'barbershop', label: 'Barbershop' },
] as const;

const PLATFORM_DEFAULT_PERCENT_BPS = 5000;
const PLATFORM_DEFAULT_ABSOLUTE_CENTS = 50000;
const VERTICAL_DEFAULTS: Record<string, { maxPercentBps: number; maxAbsoluteCents: number }> = {
  tattoo: { maxPercentBps: 6000, maxAbsoluteCents: 100000 },
  beauty: { maxPercentBps: 6000, maxAbsoluteCents: 100000 },
  aesthetics: { maxPercentBps: 5000, maxAbsoluteCents: 100000 },
  salon: { maxPercentBps: 6000, maxAbsoluteCents: 100000 },
  barbershop: { maxPercentBps: 6000, maxAbsoluteCents: 50000 },
};

type VerticalInputState = Record<string, { percent: string; absolute: string }>;

const bpsToPercentString = (bps?: number | null): string => {
  if (!Number.isFinite(bps)) return '';
  return String(Number((Number(bps) / 100).toFixed(2)));
};

const centsToDollarString = (cents?: number | null): string => {
  if (!Number.isFinite(cents)) return '';
  return String(Number((Number(cents) / 100).toFixed(2)));
};

const parsePercentToBps = (value: string): number | null => {
  const parsed = Number(value.trim());
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
};

const parseDollarsToCents = (value: string): number | null => {
  const parsed = Number(value.trim());
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
};

const formatPercentFromBps = (bps: number): string => `${(bps / 100).toFixed(2).replace(/\.00$/, '')}%`;
const formatDollarsFromCents = (cents: number): string => `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;

export const TipPolicyCard: React.FC<TipPolicyCardProps> = ({ workspaceId, activeWorkspace, onUpdate }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [defaultMaxPercent, setDefaultMaxPercent] = useState('');
  const [defaultMaxAbsolute, setDefaultMaxAbsolute] = useState('');
  const [verticalInputs, setVerticalInputs] = useState<VerticalInputState>({});

  useEffect(() => {
    setDefaultMaxPercent(bpsToPercentString(activeWorkspace.tipMaxPercentBps));
    setDefaultMaxAbsolute(centsToDollarString(activeWorkspace.tipMaxAbsoluteCents));

    const nextVerticalInputs: VerticalInputState = {};
    for (const row of VERTICAL_ROWS) {
      const current = activeWorkspace.tipMaxByVertical?.[row.key];
      nextVerticalInputs[row.key] = {
        percent: bpsToPercentString(current?.maxPercentBps),
        absolute: centsToDollarString(current?.maxAbsoluteCents),
      };
    }
    setVerticalInputs(nextVerticalInputs);
  }, [activeWorkspace]);

  const setVerticalField = (vertical: string, field: 'percent' | 'absolute', value: string) => {
    setVerticalInputs((prev) => ({
      ...prev,
      [vertical]: {
        ...(prev[vertical] || { percent: '', absolute: '' }),
        [field]: value,
      },
    }));
  };

  const resetFormToPlatformAndVerticalDefaults = () => {
    setDefaultMaxPercent('');
    setDefaultMaxAbsolute('');
    const cleared: VerticalInputState = {};
    for (const row of VERTICAL_ROWS) {
      cleared[row.key] = { percent: '', absolute: '' };
    }
    setVerticalInputs(cleared);
    sonnerToast.message('Tip policy form reset. Save to apply defaults.');
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();

    const tipMaxPercentBps = defaultMaxPercent.trim() ? parsePercentToBps(defaultMaxPercent) : null;
    const tipMaxAbsoluteCents = defaultMaxAbsolute.trim() ? parseDollarsToCents(defaultMaxAbsolute) : null;
    if (defaultMaxPercent.trim() && tipMaxPercentBps === null) {
      sonnerToast.error('Default max tip % must be a valid number.');
      return;
    }
    if (defaultMaxAbsolute.trim() && tipMaxAbsoluteCents === null) {
      sonnerToast.error('Default max tip $ must be a valid number.');
      return;
    }

    const tipMaxByVertical: Record<string, { maxPercentBps?: number | null; maxAbsoluteCents?: number | null }> = {};
    for (const row of VERTICAL_ROWS) {
      const current = verticalInputs[row.key] || { percent: '', absolute: '' };
      const percentBps = current.percent.trim() ? parsePercentToBps(current.percent) : null;
      const absoluteCents = current.absolute.trim() ? parseDollarsToCents(current.absolute) : null;

      if (current.percent.trim() && percentBps === null) {
        sonnerToast.error(`${row.label} max tip % must be a valid number.`);
        return;
      }
      if (current.absolute.trim() && absoluteCents === null) {
        sonnerToast.error(`${row.label} max tip $ must be a valid number.`);
        return;
      }

      if (percentBps !== null || absoluteCents !== null) {
        tipMaxByVertical[row.key] = {
          ...(percentBps !== null ? { maxPercentBps: percentBps } : {}),
          ...(absoluteCents !== null ? { maxAbsoluteCents: absoluteCents } : {}),
        };
      }
    }

    setIsSaving(true);
    try {
      const updateFn = httpsCallable(firebaseFunctions, 'updateWorkspaceSettings');
      await updateFn({
        workspaceId,
        tipMaxPercentBps,
        tipMaxAbsoluteCents,
        tipMaxByVertical: Object.keys(tipMaxByVertical).length > 0 ? tipMaxByVertical : null,
      });
      sonnerToast.success('Tip policy saved.');
      await onUpdate();
    } catch (error: any) {
      sonnerToast.error(`Failed to save tip policy: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const parsedDefaultPercentBps = defaultMaxPercent.trim() ? parsePercentToBps(defaultMaxPercent) : null;
  const parsedDefaultAbsoluteCents = defaultMaxAbsolute.trim() ? parseDollarsToCents(defaultMaxAbsolute) : null;
  const previewRows = [{ key: 'general', label: 'General' }, ...VERTICAL_ROWS].map((row) => {
    const verticalDefaults = VERTICAL_DEFAULTS[row.key] || {
      maxPercentBps: PLATFORM_DEFAULT_PERCENT_BPS,
      maxAbsoluteCents: PLATFORM_DEFAULT_ABSOLUTE_CENTS,
    };
    const rowInputs = verticalInputs[row.key] || { percent: '', absolute: '' };
    const rowPercentOverride = rowInputs.percent.trim() ? parsePercentToBps(rowInputs.percent) : null;
    const rowAbsoluteOverride = rowInputs.absolute.trim() ? parseDollarsToCents(rowInputs.absolute) : null;

    const percentBps =
      rowPercentOverride ??
      parsedDefaultPercentBps ??
      verticalDefaults.maxPercentBps;
    const absoluteCents =
      rowAbsoluteOverride ??
      parsedDefaultAbsoluteCents ??
      verticalDefaults.maxAbsoluteCents;

    return {
      key: row.key,
      label: row.label,
      percentBps: percentBps ?? verticalDefaults.maxPercentBps,
      absoluteCents: absoluteCents ?? verticalDefaults.maxAbsoluteCents,
    };
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl tracking-[-0.02em] text-slate-950">Tip Policy</CardTitle>
        <CardDescription className="text-slate-600">
          Set workspace-wide default gratuity caps and optional overrides by vertical. Leave blank to use platform defaults.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-900" htmlFor="tip-default-percent">
                Default Max Tip (%)
              </label>
              <Input
                id="tip-default-percent"
                inputMode="decimal"
                placeholder="e.g. 50"
                value={defaultMaxPercent}
                onChange={(e) => setDefaultMaxPercent(e.target.value)}
                disabled={isSaving}
                className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-900" htmlFor="tip-default-absolute">
                Default Max Tip ($)
              </label>
              <Input
                id="tip-default-absolute"
                inputMode="decimal"
                placeholder="e.g. 500"
                value={defaultMaxAbsolute}
                onChange={(e) => setDefaultMaxAbsolute(e.target.value)}
                disabled={isSaving}
                className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
              />
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-900">Vertical Overrides (optional)</p>
            <div className="space-y-3">
              {VERTICAL_ROWS.map((row) => (
                <div key={row.key} className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50/40 p-3 md:grid-cols-[160px_1fr_1fr] md:items-center">
                  <p className="text-sm font-medium text-slate-800">{row.label}</p>
                  <Input
                    inputMode="decimal"
                    placeholder="Max %"
                    value={verticalInputs[row.key]?.percent || ''}
                    onChange={(e) => setVerticalField(row.key, 'percent', e.target.value)}
                    disabled={isSaving}
                    className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
                    aria-label={`${row.label} max tip percent`}
                  />
                  <Input
                    inputMode="decimal"
                    placeholder="Max $"
                    value={verticalInputs[row.key]?.absolute || ''}
                    onChange={(e) => setVerticalField(row.key, 'absolute', e.target.value)}
                    disabled={isSaving}
                    className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
                    aria-label={`${row.label} max tip dollars`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-900">Effective Cap Preview</p>
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="grid grid-cols-3 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                <span>Vertical</span>
                <span>Max %</span>
                <span>Max $</span>
              </div>
              {previewRows.map((row) => (
                <div key={row.key} className="grid grid-cols-3 border-t border-slate-100 px-3 py-2 text-sm">
                  <span className="font-medium text-slate-900">{row.label}</span>
                  <span className="text-slate-700">{formatPercentFromBps(row.percentBps)}</span>
                  <span className="text-slate-700">{formatDollarsFromCents(row.absoluteCents)}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Buyers are limited by the lower of the percent cap and dollar cap. Backend validation always enforces these values.
          </p>

          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" className="rounded-xl border-slate-200 bg-white hover:bg-slate-50" onClick={resetFormToPlatformAndVerticalDefaults} disabled={isSaving}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset to defaults
            </Button>
            <Button type="submit" className="rounded-xl bg-slate-900 text-white hover:bg-slate-800" disabled={isSaving}>
              <Save className="mr-2 h-4 w-4" />
              Save Tip Policy
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
